import React, { useMemo } from 'react';
import { filter, find, findIndex, reduce } from 'lodash';
import PropTypes from 'prop-types';
import { Box, Button, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';

import { saveOaaOrgs } from '@/api';
import { AvailityHeaderLogo, Footer, OAAAdminChangeReviewTable } from '@/components';
import { useAppContext } from '@/context';
import { trackEvent } from '@/global';

// handle API call to submit OAA for unselected orgs
const submitOAA = async (payload, index) => {
  let didError = false;

  // need to wrap the passed in payload for formatting
  const payloadWrapper = {
    oAAAcknowledgements: [...payload],
    isSkip: false,
  };

  try {
    await saveOaaOrgs(payloadWrapper);
  } catch (error) {
    didError = true;

    trackEvent({
      api: 'user-org-management-api',
      event: `save-oaa-unselected-orgs-failure-${index}`,
      status: error?.response?.status,
    });
  }

  return didError;
};

const OAAAdminChangeReview = ({ ...props }) => {
  const { setCurrentOAAPage, setSkipBackPage, adminChangeFormValues, reasonOptions } = props;
  const { oaaOrgsResponse, selectedOAAOrgIds } = useAppContext();

  const { oaaOrganizations, skipCount = 0 } = oaaOrgsResponse;

  // only show the skip button if skip count is less than 3
  const showSkipButton = useMemo(() => skipCount < 3, [skipCount]);

  // extract orgs not selected - for display
  const organizationsToDisplay = useMemo(
    () => filter(oaaOrganizations, (organization) => selectedOAAOrgIds.indexOf(organization.appId) < 0),
    [selectedOAAOrgIds, oaaOrganizations]
  );

  // need to build out the api payloads for admin changes into batches of 50 orgs due to a technical limitation.
  // note that this only constructs each of the individual 'oAAAcknowledgements' objects for each unselected organization.
  const adminChangePayloads = useMemo(() => {
    let output;

    if (adminChangeFormValues && oaaOrganizations) {
      // use a reducer to put together the batches for each API call
      output = reduce(
        adminChangeFormValues,
        (result, orgValues, appId) => {
          // calculate the chunk index that the value belongs to.
          const formValueIndex = findIndex(
            Object.entries(adminChangeFormValues),
            (organizationEntry) => organizationEntry[0] === appId
          );
          const chunkIndex = Math.floor(formValueIndex / 50);

          // if the chunk doesn't exist yet, create it.
          if (!result[chunkIndex]) {
            result[chunkIndex] = [];
          }

          // build the payload object for the specific org
          // start with extracting the form values
          const { reason, admin, comments } = orgValues;

          // need to get the customer id from the original organizations array
          const organizationInfo = find(oaaOrganizations, (organization) => organization.appId === appId) || {};
          const { customerId } = organizationInfo;

          // get the reason based on the form value
          const reasonObject = find(reasonOptions, (reasonOption) => reasonOption.value === reason);

          // build the description from the new admin value and/or comments
          const newAdminString =
            admin && admin !== '' ? `If you know, who should be the new primary administrator?: ${admin}.` : '';
          const commentString = `Comments: ${comments}`;
          const description = `${newAdminString} ${commentString}`.trim();

          // construct the orgPayload
          const orgPayload = {
            appId,
            oAAAcknowledgementIn: 'N',
            oAASmartTicket: {
              customerId,
              reason: reasonObject.label,
              description,
            },
          };

          // push the orgPayload to the correct chunk
          result[chunkIndex].push(orgPayload);

          return result;
        },
        []
      );
    }

    return output;
  }, [adminChangeFormValues, oaaOrganizations, reasonOptions]);

  // handle skip navigation
  const handleSkip = () => {
    setSkipBackPage('adminChangeReview');
    setCurrentOAAPage('skipOAA');
  };

  // handle back navigation; dependent on selected orgs
  const handleBack = () => setCurrentOAAPage('adminChange');

  // submit logic
  const onSubmit = async () => {
    await Promise.all(adminChangePayloads.map(async (payload, index) => submitOAA(payload, index)));

    setCurrentOAAPage('feedback');
  };

  return (
    <Grid
      id="oaa-admin-change-review-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid size={{ xs: 12, sm: 12, md: 6 }} display="flex" flexDirection="column" alignItems="center" flexGrow={1}>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '900px', lg: '1200px' } }}
          >
            <CardContent>
              <Typography variant="h1" mb={2}>
                Review Primary Administrator Updates
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                Confirm that the information provided on the previous page is accurate. You will not be able to go back
                once you submit these primary administrator updates.
              </Typography>
              <OAAAdminChangeReviewTable
                organizations={organizationsToDisplay}
                values={adminChangeFormValues}
                reasonOptions={reasonOptions}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', marginTop: 3 }}>
              {showSkipButton ? (
                <Button color="tertiary" onClick={handleSkip}>
                  I&apos;ll do this later
                </Button>
              ) : (
                <Box />
              )}
              <Box>
                <Button color="secondary" onClick={handleBack}>
                  Back
                </Button>{' '}
                <Button color="primary" onClick={onSubmit}>
                  Submit
                </Button>
              </Box>
            </CardActions>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

OAAAdminChangeReview.propTypes = {
  setSkipBackPage: PropTypes.func.isRequired,
  setCurrentOAAPage: PropTypes.func.isRequired,
  adminChangeFormValues: PropTypes.object.isRequired,
  reasonOptions: PropTypes.array.isRequired,
};

export default OAAAdminChangeReview;
